/** Week arithmetic.
 *
 *  Weeks run Monday–Sunday by default and the start day is a **client-local**
 *  setting, because `bujo-api` has no calendar-week concept at all: its habit
 *  rollup is a rolling seven days ending today. So the grid is always built from
 *  the per-day rows returned for an explicit range, never from a service-supplied
 *  "weekly" figure, and the days-hit column is counted here over exactly the
 *  seven days on screen.
 *
 *  Range parameters are inclusive at both ends, so `from=Monday&to=Sunday`
 *  returns all seven days.
 */

export type WeekStart = 'monday' | 'sunday';

export interface Week {
  /** Seven ISO dates, in display order. */
  days: string[];
  from: string;
  to: string;
}

function parse(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function iso(at: Date): string {
  return at.toISOString().slice(0, 10);
}

export function weekOf(journalDate: string, start: WeekStart = 'monday'): Week {
  const at = parse(journalDate);
  const dow = at.getUTCDay(); // 0 = Sunday
  const offset = start === 'monday' ? (dow + 6) % 7 : dow;
  const first = parse(journalDate);
  first.setUTCDate(first.getUTCDate() - offset);
  const days: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(first);
    d.setUTCDate(first.getUTCDate() + i);
    days.push(iso(d));
  }
  return { days, from: days[0] ?? journalDate, to: days[6] ?? journalDate };
}

export function shiftWeek(week: Week, direction: -1 | 1, start: WeekStart = 'monday'): Week {
  const anchor = parse(week.from);
  anchor.setUTCDate(anchor.getUTCDate() + direction * 7);
  return weekOf(iso(anchor), start);
}

const LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** ISO-8601 week number, which is what the design's `WEEK 31` label means —
 *  verified against its own frame: 27 Jul – 02 Aug 2026 is ISO week 31. */
export function isoWeek(isoDate: string): number {
  const at = parse(isoDate);
  // Shift to the Thursday of this ISO week; the year of that Thursday owns the
  // week, which is what makes the turn of the year come out right.
  const day = (at.getUTCDay() + 6) % 7;
  at.setUTCDate(at.getUTCDate() - day + 3);
  const firstThursday = new Date(Date.UTC(at.getUTCFullYear(), 0, 4));
  const firstDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDay + 3);
  return 1 + Math.round((at.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

export function dayLabel(isoDate: string): string {
  return LABELS[parse(isoDate).getUTCDay()] ?? '';
}

export function dayNumber(isoDate: string): string {
  return isoDate.slice(8, 10);
}

/** `27 JUL – 02 AUG 2026`, and the ISO week number the label carries. */
export function weekLabel(week: Week): string {
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const a = parse(week.from);
  const b = parse(week.to);
  const left = `${week.from.slice(8, 10)} ${months[a.getUTCMonth()]}`;
  const right = `${week.to.slice(8, 10)} ${months[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
  return `${left} – ${right}`;
}
